/**
 * Recently Viewed Products Manager
 * Handles storing and retrieving product handles in localStorage
 */

class RecentlyViewedProducts {
  constructor() {
    this.storageKey = "recently_viewed_products";
    this.maxProducts = 5;
  }

  /**
   * Get recently viewed product handles from localStorage
   * @returns {Array} Array of product handles (strings)
   */
  getRecentlyViewed() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.warn("Error reading recently viewed products:", error);
      return [];
    }
  }

  /**
   * Initialize product tracking on product pages
   */
  initProductTracking() {
    // Extract handle from URL
    const pathMatch = window.location.pathname.match(/\/products\/([^/?]+)/);
    if (pathMatch && pathMatch[1]) {
      this.addProduct(pathMatch[1]);
    }
  }

  /**
   * Set maximum number of products to store
   * @param {number} max - Maximum number of products
   */
  setMaxProducts(max) {
    this.maxProducts = Math.max(1, Math.min(50, max));

    // Trim existing products if needed
    const handles = this.getRecentlyViewed();
    if (handles.length > this.maxProducts) {
      const trimmed = handles.slice(0, this.maxProducts);
      localStorage.setItem(this.storageKey, JSON.stringify(trimmed));
      this.dispatchUpdateEvent();
    }
  }

  /**
   * Check if a product handle is in recently viewed list
   * @param {string} handle - Product handle to check
   * @returns {boolean} True if product is in the list
   */
  hasProduct(handle) {
    const handles = this.getRecentlyViewed();
    return handles.includes(handle);
  }

  /**
   * Add a product handle to recently viewed list
   * @param {string} handle - Product handle
   */
  addProduct(handle) {
    if (!handle || typeof handle !== "string") {
      console.warn("Invalid product handle provided");
      return;
    }

    try {
      let recentlyViewed = this.getRecentlyViewed();

      // Remove handle if it already exists
      recentlyViewed = recentlyViewed.filter((h) => h !== handle);

      // Add to beginning of array
      recentlyViewed.unshift(handle);

      // Limit to max products
      if (recentlyViewed.length > this.maxProducts) {
        recentlyViewed = recentlyViewed.slice(0, this.maxProducts);
      }

      localStorage.setItem(this.storageKey, JSON.stringify(recentlyViewed));
      this.dispatchUpdateEvent();
    } catch (error) {
      console.error("Error adding product to recently viewed:", error);
    }
  }

  /**
   * Remove a product handle from recently viewed list
   * @param {string} handle - Product handle to remove
   */
  removeProduct(handle) {
    try {
      let recentlyViewed = this.getRecentlyViewed();
      recentlyViewed = recentlyViewed.filter((h) => h !== handle);
      localStorage.setItem(this.storageKey, JSON.stringify(recentlyViewed));
      this.dispatchUpdateEvent();
    } catch (error) {
      console.error("Error removing product from recently viewed:", error);
    }
  }

  /**
   * Clear all recently viewed products
   */
  clearAll() {
    try {
      localStorage.removeItem(this.storageKey);
      this.dispatchUpdateEvent();
    } catch (error) {
      console.error("Error clearing recently viewed products:", error);
    }
  }

  /**
   * Dispatch custom event when recently viewed list is updated
   */
  dispatchUpdateEvent() {
    const event = new CustomEvent("recentlyViewedUpdated", {
      detail: { handles: this.getRecentlyViewed() },
    });
    document.dispatchEvent(event);
  }
}

// Initialize and expose globally
const recentlyViewedManager = new RecentlyViewedProducts();

document.addEventListener("DOMContentLoaded", function () {
  recentlyViewedManager.initProductTracking();
});

window.RecentlyViewedProducts = RecentlyViewedProducts;
window.recentlyViewedManager = recentlyViewedManager;
